// Page Studio and the space designer.
export default {
  pageStudio: {
    title: "Page Studio",
    subtitle: "Build the pages of each space with the AI author: screens with their own agents, jobs and data, checked before every release.",
    newPage: "New page",
    newPageHint: "Start from a template. Describe what the page should do and the AI author drafts it for you to review.",
    createAndBuild: "Create and draft with AI",
    created: "{{name}} created.",
    searchPlaceholder: "Search pages, keys or tags",
    emptyTitle: "No pages yet",
    emptyBody: "Create the first page, or load a space pack with pnpm space:seed.",
    noMatch: "No page matches these filters.",
    personal: "Personal",
    noSpace: "Not in a space",
    updated: "updated {{when}}",
    notFound: "Page not found",
    notFoundBody: "There is no page with the key {{key}}, or you cannot author it.",
    draftCreated: "Draft v{{version}} opened.",
    built: "Build finished.",
    buildFailed: "The build failed: {{message}}",
    stageTitle: "Send {{name}} v{{version}} for release?",
    stageBody: "The draft is checked again and added to a change set. It goes live when the change set is approved and published.",
    stage: "Send for release",
    staged: "Added to the change set “{{title}}”.",
    discardTitle: "Discard draft v{{version}}?",
    discardBody: "The draft's changes are deleted. The published version stays as it is.",
    discard: "Discard draft",
    discarded: "Draft discarded.",
    runCheck: "Check the page",
    openInWorkspace: "Open in AI Workspace",
    edit: "Edit",
    build: "Build",
    hideAuthor: "Hide the AI author",
    showAuthor: "Show the AI author",
    viewingOld: "You are viewing version {{version}}, which is read-only.",
    publishedNote: "This version is published and read-only. Choose Edit to open a draft.",
    authorTitle: "AI author",
    authorDraftNote: "The AI author works on a draft, so nothing changes for people using the page until you release it.",
    authorCan: {
      plan: "Plans the change and lists its steps",
      edit: "Writes and fixes the code, agents and jobs",
      check: "Builds the page and checks it in English and Arabic",
      data: "Finds and imports data, and requests packages or capabilities",
    },
    editWithAuthor: "Open a draft with the AI author",
    help: {
      title: "Page Studio",
      what: "Custom pages are the screens of a space: dashboards, workbenches, registers and home widgets. Each page is a small React project with its own agents, scheduled jobs and data, running in a sandbox with only the capabilities it declares.",
      author: "The AI author writes and fixes the code with you. It plans, edits files, builds, checks the page in English and Arabic, imports data, and asks the platform team for a package or a capability the page needs.",
      release: "Every save is checked. Before release the page is built, rendered with an accessibility check in both languages, and its agents run their evaluations. Releases go through a change set.",
    },
    galleryTabs: { pages: "Pages", suggestions: "Suggestions", requests: "Requests" },
    stats: {
      pages: "Space pages",
      pagesHint_one: "In {{count}} space",
      pagesHint_other: "Across {{count}} spaces",
      drafts: "Open drafts",
      draftsHint: "Changes not yet released",
      failing: "Failing checks",
      failingHint: "To fix before the next release",
      personal: "Your personal pages",
      personalHint: "Built from chat; share or promote them",
    },
    filters: { space: "Space", kind: "Kind", spacePages: "Space pages", personalPages: "Personal pages" },
    fields: {
      title: "Title",
      titleAr: "Arabic title",
      key: "Key",
      keyHint: "The page's address in the AI Workspace: /p/key.",
      keyInvalid: "Use 2 to 63 lower-case letters, digits or hyphens, starting with a letter.",
      space: "Space",
      spaceHint: "The space that owns the page. Other spaces can mount it later.",
      template: "Start from",
      description: "What should the page do?",
      descriptionHint: "Optional. The AI author drafts the page from this as soon as it opens.",
      descriptionPlaceholder: "For example: track tourism arrivals by emirate each quarter, with a forecast to 2031 and an agent that explains the drivers.",
    },
    templates: {
      dashboard: { name: "Dashboard", description: "Indicators, a chart and a table over one dataset, with an agent that answers questions about it." },
      workbench: { name: "Matter workbench", description: "A tab on a matter with its facts, a worksheet and an agent that drafts into it." },
      tracker: { name: "Tracker", description: "A register with stages, such as policy files, campaigns or requests." },
      widget: { name: "Home widget", description: "A small card for a space's home." },
      blank: { name: "Blank", description: "An empty React page with the page kit." },
    },
    tabs: { preview: "Preview", code: "Code", data: "Data", agents: "Agents", jobs: "Jobs", versions: "Versions", validation: "Checks", suggestions: "Suggestions", usage: "Usage" },
    status: { Draft: "Draft", InReview: "In review", Published: "Published", Retired: "Retired" },
    check: { pass: "Checks pass", warn: "Warnings", fail: "Checks fail", skipped: "Not checked" },
    slots: { home: "Home", nav: "Navigation", "work-tab": "Matter tab", workbench: "Workbench", widget: "Widget" },
    severity: { error: "Error", warning: "Warning", info: "Note" },
    origin: { personal: "built from chat", suggestion: "suggested change", promoted: "promoted from a personal page", repo: "from the {{folder}} pack", import: "imported", seed: "seeded" },
    preview: {
      navigates: "The page asked to open another screen. Links work in the AI Workspace.",
      language: "Language",
      both: "Both",
      width: "Width",
      desktop: "Desktop",
      tablet: "Tablet",
      phone: "Phone",
      slot: "Shown as",
      space: "In space",
      reload: "Reload",
      draftNote: "You are previewing draft v{{version}}. Only authors see it until it is released.",
      matter: "On matter",
      noMatter: "Matter tabs show a matter's data, and this space has no matters yet. The page shows its empty state until one exists.",
    },
    code: {
      saved: "Saved.",
      savedWithErrors_one: "Saved with {{count}} error.",
      savedWithErrors_other: "Saved with {{count}} errors.",
      savedWithWarnings_one: "Saved with {{count}} warning.",
      savedWithWarnings_other: "Saved with {{count}} warnings.",
      deleteTitle: "Delete {{path}}?",
      deleteBody: "The file is removed from the draft. Earlier versions keep it.",
      files_one: "{{count}} file",
      files_other: "{{count}} files",
      newFile: "New file",
      newFileHint: "A path inside the project, for example src/components/Chart.tsx or agents/analyst.md.",
      path: "Path",
      exists: "A file with this path already exists.",
      readOnly: "Read-only",
      notePlaceholder: "What changed (optional)",
      revert: "Revert",
      saveAll_one: "Save {{count}} file",
      saveAll_other: "Save {{count}} files",
      conflictTitle: "This file changed since you opened it",
      conflictBody: "Someone, or the AI author, saved a newer copy. Saving now replaces it.",
      takeTheirs: "Load their copy",
      binary: "A binary file ({{type}}). Replace it with a new upload.",
    },
    data: {
      collections: "Collections",
      collectionsHint: "Records the page keeps, as declared in page.json, with what is stored now.",
      noCollections: "The page declares no collections.",
      scope: { user: "Per person", space: "Per space", shared: "Every space" },
      records_one: "{{count}} record",
      records_other: "{{count}} records",
      noRecords: "Nothing stored yet.",
      retention: "Kept for {{days}} days",
      datasets: "Datasets",
      datasetsHint: "Tables imported for this page, with their columns, a sample and where they came from.",
      noDatasets: "No datasets have been imported for this page.",
      origin: { upload: "Upload", outreach: "Outreach", source: "Source", derived: "Derived", collector: "Collector", function: "Function", agent: "Agent" },
      rows_one: "{{count}} row",
      rows_other: "{{count}} rows",
      source: "Source",
      reads: "Platform data read",
      readsHint: "What the page read from the platform over the last 30 days, by capability.",
      noReads: "The page has not read platform data in the last 30 days.",
      mocks: "Sample data",
      mocksHint: "mocks/datasets.json: the data the page uses in previews and checks.",
      noMocks: "The page has no sample data.",
    },
    agents: {
      emptyTitle: "No agents",
      emptyBody: "Add an agent to page.json, or ask the AI author to add one.",
      openFile: "Open the file",
      definition: "Agent definition",
      tools: "Tools",
      knowledge: "Knowledge",
      guardrails: "Guardrails",
      standard: "Standard",
      budget: "Budget",
      tokensPerRun: "{{count}} tokens a run",
      month: "month",
      instructions: "Instructions",
      evals: "Evaluations ({{count}})",
      evalError: "The evaluation could not run.",
      evalResult: "{{passed}} of {{total}} passed · red team {{red}} of {{redTotal}}",
      notEvaluated: "Not evaluated yet. Run the full check.",
      clientTools: "Tools the page gives its agents",
      clientToolsHint: "Actions the agents can take inside the open page, such as filtering a chart.",
    },
    jobs: {
      emptyTitle: "No jobs",
      emptyBody: "Jobs run an agent, a function or a model on a schedule or when something happens. Declare them in page.json.",
      started: "Run started.",
      schedule: "Schedule {{cron}}",
      event: "When {{event}}",
      manual: "Run by hand",
      runsAgent: "runs agent {{agent}}",
      runsFunction: "runs function {{name}}",
      runsModel: "runs model {{name}}",
      draftNote: "Jobs go live when the page is released. Until then they are listed here but do not run.",
      notLive: "Not live",
      digest: "Digest",
      owner: "owned by {{name}}",
      runNow: "Run now",
      automation: "Automation",
      next: "Next {{when}}",
      last: "last {{when}}",
      counts: "{{runs}} runs, {{failures}} failed",
      noRuns: "No runs yet.",
      status: { Succeeded: "Succeeded", Failed: "Failed", Skipped: "Skipped", DryRun: "Dry run" },
    },
    versions: {
      restoreTitle: "Restore version {{version}}?",
      restoreBody: "Its files are copied into the draft (one is opened if needed). Nothing goes live until the draft is released.",
      restore: "Restore",
      restoreNote: "Restored from version {{version}}",
      restored: "Version {{version}} restored into draft v{{draft}}.",
      published: "published {{when}}",
      updated: "updated {{when}}",
      files_one: "{{count}} file",
      files_other: "{{count}} files",
      backToNewest: "Back to the newest version",
      compare: "Compare",
      newest: "Newest",
      compareTitle: "Changes from v{{from}} to v{{to}}",
      compareHint: "Lines removed are red and lines added are green.",
    },
    validation: {
      failed_one: "{{count}} check failed.",
      failed_other: "{{count}} checks failed.",
      warnings_one: "Passed with {{count}} warning.",
      warnings_other: "Passed with {{count}} warnings.",
      passed: "All checks passed.",
      projectIssues: "Problems in the project",
    },
    suggestions: {
      emptyTitle: "No suggestions",
      emptyBody: "People suggest changes with Suggest a change in the AI Workspace. They appear here for review.",
      inboxEmpty: "Nothing to review",
      inboxEmptyBody: "Suggested changes and personal pages put forward for a space appear here.",
      open: "Open",
      withDecided: "Including decided",
      noneWaiting: "Nothing is waiting for review.",
      status: { Drafting: "Drafting", Ready: "Ready", Submitted: "Waiting for review", Accepted: "Accepted", Declined: "Declined", Withdrawn: "Withdrawn", Failed: "Failed" },
      kind: { improve: "Change", promote: "Into a space" },
      files_one: "{{count}} file",
      files_other: "{{count}} files",
      from: "From {{name}}, {{when}}",
      promoteInto: "Into {{space}} as {{key}}",
      basedOn: "Based on v{{version}}",
      summary: "What the AI author changed",
      changes_one: "Changes ({{count}} file)",
      changes_other: "Changes ({{count}} files)",
      preview: "Preview",
      note: "Note to the requester",
      noteHint: "Sent with your decision.",
      accept: "Accept into the draft",
      acceptPromote: "Accept into the space",
      decline: "Decline",
      acceptHint: "The changes are merged into the page's draft. Release the draft to make them live.",
      acceptPromoteHint: "A space page is created from the personal page as a draft. Release it to make it live.",
      accepted: "Suggestion accepted.",
      declined: "Suggestion declined.",
      decided: "Decided by {{name}}, {{when}}",
    },
    usage: {
      window: "Period",
      days_one: "Last {{count}} day",
      days_other: "Last {{count}} days",
      opens: "Opens",
      people: "People",
      turns: "Agent turns",
      refused_one: "{{count}} refused",
      refused_other: "{{count}} refused",
      agentsUsed_one: "By {{count}} agent",
      agentsUsed_other: "By {{count}} agents",
      spend: "Model spend",
      budget: "{{spent}} of {{budget}} this month",
      noBudget: "No monthly budget",
      perDay: "Use per day",
      none: "No use in this period.",
      byUser: "By person",
      byUserHint: "Who uses the page and how.",
      person: "Person",
      calls: "Calls",
      capabilities: "Capabilities called",
      capabilitiesHint: "Platform capabilities the page called, with how many were refused.",
      agents: "Agents",
      bySpace: "By space",
      kind: { open: "Opens", agent: "Agent turns", call: "Calls", job: "Job runs", export: "Exports", improve: "Suggestions", error: "Errors" },
      outcome: { denied: "Refused", limited: "Over a limit", error: "Failed", invalid: "Invalid input" },
    },
    requests: {
      packages: "Package requests",
      packagesHint: "Packages pages may import come from the approved store. Authors and the AI author request new ones here.",
      packageStatus: { Requested: "Requested", Approved: "Approved", Rejected: "Rejected" },
      noPackages: "No package requests.",
      builderDown: "The page builder is not reachable, so approved packages are not loaded yet.",
      capabilities: "Capability requests",
      capabilitiesHint: "When no platform capability fits, the AI author files a request with a draft specification for the platform team.",
      requestStatus: { Open: "Open", Accepted: "Accepted", Declined: "Declined", Delivered: "Delivered" },
      noRequests: "No capability requests.",
      approved: "{{name}} approved.",
      rejected: "{{name}} rejected.",
      vendor: "Vendored",
      licence: "Licence",
      note: "Note",
      approve: "Approve",
      reject: "Reject",
      decided: "Decided by {{name}}, {{when}}",
      resolved: "Marked as {{status}}.",
      resolution: "Resolution",
      resolutionPlaceholder: "What was decided or delivered",
      mark: { Accepted: "Accept", Delivered: "Delivered", Declined: "Decline" },
    },
  },
  spaceDesigner: {
    title: "Space design",
    intro: "What people see in this space in the AI Workspace: its home, navigation, use cases and the tabs on its matters. Pages come from Page Studio.",
    packNote: "This design came from the {{folder}} pack. Once you save it here, later pack updates no longer replace it.",
    source: { repo: "From the {{folder}} pack", generated: "Standard, until a pack or a save replaces it" },
    open: "Open the space",
    reset: "Undo changes",
    save: "Save design",
    saved: "Space design saved.",
    notSaved: "The design was not saved",
    issues: "Problems in the saved design",
    noPageList: "The page list is not available to you, so enter page keys by hand.",
    jsonHint: "Edit the design as JSON. Switch back to the visual view to save.",
    textEn: "English",
    textAr: "Arabic",
    pageKey: "Page key",
    choosePage: "Choose a page",
    otherSlot: "not offered for this place",
    missingPage: "no such page",
    home: {
      title: "Home",
      hint: "The sections of the space's home, top to bottom.",
      empty: "The home shows the standard sections.",
      addPage: "Page",
      addUseCases: "Use cases",
      addWidget: "Built-in widget",
      kind: { page: "Page", "use-cases": "Use cases", builtin: "Built-in widget" },
      useCasesNote_one: "Shows the space's {{count}} use case.",
      useCasesNote_other: "Shows the space's {{count}} use cases.",
      width: "Width",
      full: "Full width",
      half: "Half width",
      titlePlaceholder: "Section title (optional)",
    },
    nav: {
      title: "Navigation",
      hint: "The space's menu. Work holds daily tools, Insight the analysis pages, Resources the reference material.",
      sections: { work: "Work", insight: "Insight", resources: "Resources" },
      addPage: "Page",
      addBuiltin: "Screen",
      empty: "Nothing here.",
      labelPlaceholder: "Menu label",
      icon: "Icon",
      section: "Section",
      group: "Group",
      permission: "Permission",
    },
    builtin: { home: "Home", chat: "Chat", inbox: "Inbox", matters: "Matters", watchers: "Watchers", artifacts: "Outputs", knowledge: "Knowledge", evidence: "Evidence", tracker: "Tracker", library: "Library" },
    matters: {
      title: "Matter tabs",
      hint: "For each playbook, the tabs a matter shows, grouped by stage. A group can be limited to the matter states it applies to.",
      customise: "Set tabs here",
      stage: "Stage key",
      stageLabel: "Stage label",
      states: "Matter states, comma-separated (empty for all)",
      tabLabel: "Tab label (optional)",
      when: "Only with template key",
      whenHint: "Show the tab only on matters whose use-case template has this key, for example sandbox.",
      addPageTab: "Page tab",
      addBuiltinTab: "Built-in tab",
      addStage: "Stage",
    },
    workbench: {
      title: "Workbench",
      hint: "Pages that open full-size on a matter, from a button in its header.",
      empty: "No workbench pages.",
    },
    mounts: {
      title: "Pages from other spaces",
      hint: "Pages owned by another space that people here can open too.",
      empty: "No pages mounted.",
    },
    useCases: {
      title: "Use cases",
      hint: "Ready-made starting points on the home: each starts a playbook, opens a page or asks the assistant.",
      add: "Use case",
      empty: "No use cases.",
      ready: "Ready",
      proposed: "Proposed",
      duration: "Duration",
      titlePlaceholder: "Title",
      descriptionPlaceholder: "What it does (optional)",
      producesPlaceholder: "What it produces (optional)",
      start: "Starts",
      startPlaybook: "A playbook",
      startPage: "A page",
      startPrompt: "A question to the assistant",
      promptPlaceholder: "What the assistant is asked",
      tags: "Tags, comma-separated",
    },
    grants: {
      title: "Grants",
      hint: "Extra capabilities pages hold in this space, beyond what every page gets, such as outreach or writing to the platform.",
      empty: "No page holds extra grants.",
      placeholder: "Grants, comma-separated",
      add: "Grant",
    },
    look: {
      title: "Look and words",
      hint: "The theme pages use in this space, and the vocabulary that renames matters and cases.",
      theme: "Theme",
      defaultTheme: "Platform default",
      themes: { ncp: "Platform gold", seablue: "Sea blue", camel: "Camel", aegreen: "Emirates green", slate: "Slate" },
      vocabulary: "Vocabulary",
      standardWords: "Standard words",
    },
    usage: {
      title: "Usage",
      hint: "How the space's pages were used.",
      pages: "Pages used",
      page: "Page",
      opens: "Opens",
      people: "People",
      turns: "Agent turns",
      calls: "Calls",
      errors: "Errors",
      refused: "Refused",
      suggestions: "Suggestions",
    },
  },
} as const;
